import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import { secureGet, secureDelete } from "../../config/api";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatAmount } from "../../utils/format";
import OptionsMenu from "../../components/OptionsMenu";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import ModuleWrapper from "../../components/ModuleWrapper";
import UserContext from "../../Shared/UserContext";
import SnackbarNotification from "../../components/SnackbarNotification";
import LoadingLogo from "../../components/LoadingLogo";
import Filter from "../../components/Filter";
import ProductCard from "./ProductCard";
import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const PAGE_SIZE = 30;

const Services = () => {
  const history = useHistory();
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);
  const { t } = useTranslation();
  const topRef = useRef(null);

  const [currentPageData, setCurrentPageData] = useState([]);
  const [moduleData, setModuleData] = useState([]); // Keep for delete operations
  const [filterValues, setFilterValues] = useState({ needle: "", familyId: "" });
  const [, setneedle] = useState("");
  const [filterOptions, setFilterOptions] = useState({ families: [] });

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [showModule, setShowModule] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success", manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  // fetch paginated services
  const fetchPaginatedServices = useCallback(async (page = 0, size = pageSize, activeFilters = filterValues) => {
    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("page", page.toString());
      params.set("size", size.toString());

      if (activeFilters.needle) {
        params.set("needle", activeFilters.needle);
      }

      if (activeFilters.familyId) {
        params.set("familyId", activeFilters.familyId);
      }



      const endpoint = `/products/byFamilyType/SERVICE?${params.toString()}`;
      const response = await secureGet(endpoint, showSnackbar, { signal });

      const { content, totalPages: responseTotalPages } = response.data;
      setCurrentPageData(content);
      setModuleData(content); // Keep for delete operations
      setTotalPages(responseTotalPages);
      setPageNumber(page);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching services:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize, filterValues, signal, showSnackbar]);

  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        const familiesResponse = await secureGet('/families/type/SERVICE', showSnackbar, { signal });
        setFilterOptions((prev) => ({
          ...prev,
          families: familiesResponse.data.map((item) => ({ label: item.name, value: item.id })),
        }));
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching families:', error);
        }
      }
    };
    fetchFamilies();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const stateFilters = location.state?.filters || { needle: "", familyId: "" };
    const familyIdFromState = location.state?.familyId || "";
    const newFilters = { ...stateFilters, familyId: familyIdFromState || stateFilters.familyId || "" };
    const statePage = location.state?.page ?? 0;
    const stateSize = location.state?.size ?? PAGE_SIZE;

    setFilterValues(newFilters);
    setPageNumber(statePage);
    setPageSize(stateSize);
    fetchPaginatedServices(statePage, stateSize, newFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSearchChange = (value) => {
    setneedle(value);
    setPageNumber(0);
    const updatedFilters = { ...filterValues, needle: value };
    setFilterValues(updatedFilters);
    fetchPaginatedServices(0, pageSize, updatedFilters);
  };

  const handleFilterChange = (name, value) => {
    const updatedFilters = { ...filterValues, [name]: value };
    setFilterValues(updatedFilters);
    setPageNumber(0);
    if (name === "needle") {
      handleSearchChange(value);
    } else {
      fetchPaginatedServices(0, pageSize, updatedFilters);
    }
  };

  const resetFilters = () => {
    const newFilterValues = { needle: "", familyId: "" };
    setFilterValues(newFilterValues);
    setPageNumber(0);
    fetchPaginatedServices(0, pageSize, newFilterValues);
  };

  const handleAddClick = () => {
    setSelectedService(null);
    setShowModule(true);
  };

  const handleEditClick = (service) => {
    setSelectedService(service);
    setShowModule(true);
  };

  const handleDeleteClick = (service) => {
    setItemToDelete(service);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await secureDelete(`/products/delete/${itemToDelete.id}`, showSnackbar);
      showSnackbar(t("confirmation.serviceDelete") || "Service supprimé avec succès", "success");
      // Refresh current page after deletion
      fetchPaginatedServices(pageNumber, pageSize, filterValues);
    } catch (e) {
      console.error("Error deleting service:", e);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleModuleClose = useCallback((action) => {
    if (action === "submit") fetchPaginatedServices(pageNumber, pageSize, filterValues); // refresh after add/modify
    setShowModule(false);
    setSelectedService(null);
  }, [fetchPaginatedServices, pageNumber, pageSize, filterValues]);

  const getOptions = useCallback(
    (service) => [
      { label: t("actions.modify"), icon: EditIcon, action: () => handleEditClick(service) },
      { label: t("actions.delete"), icon: DeleteIcon, color: "#EF476F", action: () => handleDeleteClick(service) },
    ],
    [t]
  );

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setPageNumber(page);
      fetchPaginatedServices(page, pageSize, filterValues);
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(0);
    fetchPaginatedServices(0, newPageSize, filterValues);
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t("titles.services")}</title>
      </Helmet>

      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.services")}</h1>
        {(role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_STOREKEEPER" || role === "ROLE_WORKSHOP_MANAGER") && (
          <PrimaryButton label={t("primaryButtons.add_service")} icon={AddIcon} onClick={handleAddClick} />
        )}
      </div>

      <Filter
        filters={[
          { name: "needle", type: "search" },
          { name: "familyId", type: "dropdown", label: t(`filters.products.family`), options: filterOptions.families },
        ]}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
      />

      <div className="relative">
        {loading && (
          <div className="absolute inset-0 flex justify-center items-center z-10 pt-20">
            <LoadingLogo />
          </div>
        )}

        <div
          className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        >
          {currentPageData.length > 0 ? (
            currentPageData.map((item) => (
              <div key={item.id} style={{ position: "relative" }}>
              <ProductCard
              image={item.image}
              name={item.name}
              price={item.priceTTC}
              discount={item.discount}
              reference={item.reference}
              optionsMenu={
                <div onClick={(e) => e.stopPropagation()}>
                  {(role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_STOREKEEPER" || role === "ROLE_WORKSHOP_MANAGER") && <OptionsMenu options={getOptions(item)} />}
                </div>
              }
              />
              </div>
            ))
          ) : (
            <p className="text-gray-600 text-center w-full">
            </p>
          )}
        </div>
      </div>

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {showModule && (
        <ModuleWrapper
          moduleType="products"
          title={t("primaryButtons.add_service")}
          mode={selectedService ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={selectedService ? { ...selectedService } : { familyType: "SERVICE" }}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Services;
